import { motion } from "framer-motion";
import {
  HandHeart,
  House,
  Heart,
  Pill,
  FirstAidKit,
  Brain,
  ForkKnife,
  PersonSimpleWalk,
  UsersThree,
  Broom,
  ArrowUpRight,
} from "@phosphor-icons/react";

const ICONS = {
  HandHeart, House, Heart, Pill, FirstAidKit, Brain,
  ForkKnife, PersonSimpleWalk, UsersThree, Broom,
};

const SERVICES = [
  { icon: "HandHeart", name: "Personal Care Assistance",
    desc: "Daily hygiene, dressing, and grooming support delivered with quiet dignity.",
    span: "lg:col-span-4 lg:row-span-2", tone: "primary" },
  { icon: "House", name: "Senior Home Care",
    desc: "Comprehensive in-home support so seniors can age safely where they feel most at home.",
    span: "lg:col-span-3", tone: "soft" },
  { icon: "Heart", name: "Companionship",
    desc: "Friendly visits, conversation, and shared activities that ease loneliness.",
    span: "lg:col-span-3", tone: "soft" },
  { icon: "Pill", name: "Medication Reminders",
    desc: "Gentle prompts and routines to keep prescriptions on schedule and on track.",
    span: "lg:col-span-2", tone: "accent" },
  { icon: "FirstAidKit", name: "Post-Surgery Care",
    desc: "Calm, structured recovery support at home after a hospital stay or surgery.",
    span: "lg:col-span-3", tone: "soft" },
  { icon: "Brain", name: "Dementia & Alzheimer’s Support",
    desc: "Specialized memory-care routines that bring patience, structure, and reassurance.",
    span: "lg:col-span-3", tone: "primary" },
  { icon: "ForkKnife", name: "Meal Preparation",
    desc: "Wholesome, dietitian-friendly meals cooked in your own kitchen.",
    span: "lg:col-span-2", tone: "accent" },
  { icon: "PersonSimpleWalk", name: "Mobility Assistance",
    desc: "Safe transfers, walking support, and fall-prevention practices.",
    span: "lg:col-span-3", tone: "soft" },
  { icon: "UsersThree", name: "Respite Care for Families",
    desc: "Short-term relief that lets family caregivers rest, recharge, and return refreshed.",
    span: "lg:col-span-3", tone: "soft" },
  { icon: "Broom", name: "Light Housekeeping",
    desc: "Tidying, laundry, and a calmer home environment that supports wellness.",
    span: "lg:col-span-2", tone: "accent" },
];

function toneClasses(tone) {
  switch (tone) {
    case "primary":
      return "bg-[#1E5F8C] text-[#F8FAFC] border-transparent";
    case "accent":
      return "bg-[#DCEAF2] text-[#0F2942] border-[#1E5F8C]/5";
    default:
      return "bg-[#EEF4F8] text-[#0F2942] border-[#1E5F8C]/5";
  }
}

export default function Services() {
  return (
    <section id="services" className="py-20 md:py-32" data-testid="services-section">
      <div className="container-pad mx-auto max-w-7xl">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-5">
              What we do
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F2942] leading-[1.1]" data-testid="services-heading">
              Care designed around your loved one, not the other way around.
            </h2>
          </div>
          <p className="text-[#475569] max-w-md text-base md:text-lg">
            Ten specialised services, one consistent caregiver. Mix and match
            to build a plan that fits your family’s rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-10 auto-rows-[1fr] gap-4 md:gap-5">
          {SERVICES.map((svc, idx) => {
            const Icon = ICONS[svc.icon];
            return (
              <motion.article
                key={svc.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (idx % 5) * 0.05 }}
                className={`group relative rounded-3xl p-7 md:p-8 border ${toneClasses(svc.tone)} ${svc.span} hover:-translate-y-1 transition-transform duration-300 hover:shadow-[0_18px_40px_rgba(43,76,62,0.10)]`}
                data-testid={`service-card-${svc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-5 ${
                  svc.tone === "primary"
                    ? "bg-[#F8FAFC]/10 text-[#F8FAFC]"
                    : "bg-[#F8FAFC] text-[#1E5F8C]"
                }`}>
                  <Icon size={26} weight="duotone" />
                </div>
                <h3 className={`font-serif text-xl md:text-2xl leading-tight ${
                  svc.tone === "primary" ? "text-[#F8FAFC]" : "text-[#0F2942]"
                }`}>
                  {svc.name}
                </h3>
                <p className={`mt-3 text-sm md:text-base leading-relaxed ${
                  svc.tone === "primary" ? "text-[#F8FAFC]/80" : "text-[#475569]"
                }`}>
                  {svc.desc}
                </p>

                <a
                  href="#booking"
                  className={`mt-6 inline-flex items-center gap-1.5 text-sm font-medium transition-opacity ${
                    svc.tone === "primary" ? "text-[#F8FAFC]" : "text-[#1E5F8C]"
                  } opacity-70 group-hover:opacity-100`}
                >
                  Request care <ArrowUpRight size={16} weight="bold" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
